import { useState, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

/**
 * useApiCrud — Generic CRUD operations with loading state and error toasts.
 * Replaces repeated axios + try/catch + loading + toast patterns across 15+ pages.
 *
 * Usage:
 *   const { data, loading, fetchData, createItem, updateItem, deleteItem } = useApiCrud("/appointments");
 *   // fetchData() — GET with optional params
 *   // createItem(body) — POST
 *   // updateItem(id, body) — PUT
 *   // deleteItem(id) — DELETE
 */
export function useApiCrud(basePath, options = {}) {
  const {
    onSuccess,
    onError,
    idField = "id",       // URL path param name, e.g., /appointments/:id
    successMsg,
    errorMsg,
    autoFetch = false,    // If true, fetch on mount (not implemented here to avoid hook rules issues — use useEffect)
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  // GET — fetch list or single item
  const fetchData = useCallback(async (params = {}, pathSuffix = "") => {
    setLoading(true);
    setError(null);

    // Cancel previous request if still in flight
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const queryString = new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v != null))
      ).toString();
      const url = `${API}${basePath}${pathSuffix}${queryString ? "?" + queryString : ""}`;
      const res = await axios.get(url, {
        withCredentials: true,
        signal: controller.signal,
      });
      setData(res.data);
      return res.data;
    } catch (err) {
      if (axios.isCancel(err)) return;
      const msg = errorMsg || "Failed to load data";
      setError(msg);
      toast.error(msg);
      onError?.(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [basePath, errorMsg, onError]);

  // POST — create item
  const createItem = useCallback(async (body, pathSuffix = "") => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}${basePath}${pathSuffix}`, body, {
        withCredentials: true,
      });
      const msg = successMsg || "Created successfully";
      toast.success(msg);
      onSuccess?.(res.data);
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.detail || errorMsg || "Failed to create";
      toast.error(msg);
      onError?.(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [basePath, successMsg, errorMsg, onSuccess, onError]);

  // PUT — update item
  const updateItem = useCallback(async (id, body, pathSuffix = "") => {
    setLoading(true);
    try {
      const url = id
        ? `${API}${basePath}/${id}${pathSuffix}`
        : `${API}${basePath}${pathSuffix}`;
      const res = await axios.put(url, body, { withCredentials: true });
      const msg = successMsg || "Updated successfully";
      toast.success(msg);
      onSuccess?.(res.data);
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.detail || errorMsg || "Failed to update";
      toast.error(msg);
      onError?.(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [basePath, successMsg, errorMsg, onSuccess, onError]);

  // DELETE — remove item
  const deleteItem = useCallback(async (id, pathSuffix = "") => {
    setLoading(true);
    try {
      const res = await axios.delete(`${API}${basePath}/${id}${pathSuffix}`, {
        withCredentials: true,
      });
      const msg = successMsg || "Deleted successfully";
      toast.success(msg);
      onSuccess?.(res.data);
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.detail || errorMsg || "Failed to delete";
      toast.error(msg);
      onError?.(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [basePath, successMsg, errorMsg, onSuccess, onError]);

  // Raw request — for custom endpoints
  const request = useCallback(async (method, path, body = null) => {
    setLoading(true);
    try {
      const config = { withCredentials: true };
      let res;
      if (method === "get") {
        res = await axios.get(`${API}${path}`, config);
      } else if (method === "post") {
        res = await axios.post(`${API}${path}`, body, config);
      } else if (method === "put") {
        res = await axios.put(`${API}${path}`, body, config);
      } else if (method === "delete") {
        res = await axios.delete(`${API}${path}`, config);
      }
      return res?.data;
    } catch (err) {
      const msg = err.response?.data?.detail || "Request failed";
      toast.error(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    data,
    setData,
    loading,
    error,
    fetchData,
    createItem,
    updateItem,
    deleteItem,
    request,
  };
}
